import { Group } from "@visx/group";
import {
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import isEqual from "lodash-es/isEqual";
import cx from "classnames";

import Icon from "ds/components/Icon";

import styles from "./styles.module.css";
import { Position } from "../types";
import { createEndId, createStartId } from "../utils";
import {
  ICON_SIZE,
  ICON_SPACE,
  CONNECTION_SPACE,
  CONNECTION_DY,
  SUMMARY_GAP,
  SUMMARY_ITEM_WIDTH,
} from "./contstants";
import { TreeChartGroupNodeProps } from "./types";
import TreeChartGroupNodeRects from "./Rects";
import TreeChartGroupNodeSummary from "./Summary";

const TreeChartGroupNode = ({
  id,
  item,
  setConnectionPoints,
  connectionPoints,
  columnWidth,
  onMouseEnter,
  onMouseLeave,
  activeId,
}: TreeChartGroupNodeProps) => {
  const textRef = useRef<SVGTextElement>(null);
  const [tooltipActive, setTooltipActive] = useState(false);
  const [summaryX, setSummaryX] = useState(0);

  const createMouseEnterHandler = useCallback(
    (tooltipValue: ReactNode) => (event: React.MouseEvent<SVGTextElement | SVGRectElement>) => {
      const position = (event.target as SVGTextElement | SVGRectElement).getBBox();
      const x = position.x + item.position.x + position.width / 2;
      const y = position.y + item.position.y;

      onMouseEnter(tooltipValue, { x, y });
    },
    [onMouseEnter, item.position.x, item.position.y]
  );

  const iconWidthAndSpace = item.icon ? ICON_SIZE + ICON_SPACE : 0;

  const summaryWidth = item.statuses?.length
    ? item.statuses.length * SUMMARY_ITEM_WIDTH + SUMMARY_GAP
    : 0;

  useLayoutEffect(() => {
    // Set X position for summary
    if (textRef.current) {
      const newPosition = textRef.current.getBBox().width + SUMMARY_GAP + iconWidthAndSpace;

      if (newPosition !== summaryX) {
        setSummaryX(newPosition);
      }
    }

    // Text ellipsis
    const summaryElemSpace = item.group?.length ? summaryWidth : 0;

    if (textRef.current) {
      let newText = item.name;
      let textLength = textRef.current.getComputedTextLength();

      while (textLength > 0 && textLength > columnWidth - summaryElemSpace) {
        if (!tooltipActive) {
          setTooltipActive(true);
        }

        newText = newText.slice(0, -1).trim();
        textRef.current.textContent = `${newText}...`;
        textLength = textRef.current.getComputedTextLength();
      }
    }

    // Set connection points
    const startPointID = createStartId(id);
    const startPointPosition = {
      x: item.position.x - CONNECTION_SPACE,
      y: item.position.y + CONNECTION_DY,
    };

    const endPointID = createEndId(id);
    const endPointPosition = {
      x:
        item.position.x +
        CONNECTION_SPACE +
        (textRef?.current?.getBBox().width || 0) +
        summaryElemSpace +
        iconWidthAndSpace,
      y: item.position.y + CONNECTION_DY,
    };

    if (
      !isEqual(startPointPosition, connectionPoints[startPointID]) ||
      !isEqual(endPointPosition, connectionPoints[endPointID])
    ) {
      setConnectionPoints(
        (connectionPoints) =>
          ({
            ...connectionPoints,
            [startPointID]: startPointPosition,
            [endPointID]: endPointPosition,
          }) as Record<string, Position>
      );
    }
  }, [
    connectionPoints,
    item.position.x,
    item.position.y,
    id,
    setConnectionPoints,
    columnWidth,
    item.name,
    item.group?.length,
    tooltipActive,
    iconWidthAndSpace,
    summaryWidth,
    summaryX,
  ]);

  return (
    <Group className={styles.groupNode} left={item.position.x} top={item.position.y} id={id}>
      <Group className={cx(styles.groupNode)}>
        {item.icon ? <Icon src={item.icon} /> : null}

        <text
          dominantBaseline="hanging"
          textAnchor="start"
          dx={iconWidthAndSpace}
          className={styles.text}
          ref={textRef}
          aria-label={item.name}
          onMouseLeave={tooltipActive ? onMouseLeave : undefined}
          onMouseEnter={tooltipActive ? createMouseEnterHandler(item.name) : undefined}
        >
          {item.name}
        </text>

        {item.group && item.statuses && (
          <>
            <TreeChartGroupNodeSummary
              startX={summaryX}
              createMouseEnterHandler={createMouseEnterHandler}
              onMouseLeave={onMouseLeave}
              items={item.statuses}
            />

            <TreeChartGroupNodeRects
              items={item.group}
              activeId={activeId}
              createMouseEnterHandler={createMouseEnterHandler}
              onMouseLeave={onMouseLeave}
            />
          </>
        )}
      </Group>
    </Group>
  );
};

export default memo(TreeChartGroupNode) as (p: TreeChartGroupNodeProps) => ReactElement;
